import type { App } from 'vue'
import { createI18n } from 'vue-i18n'

import en from './default/en'
import es from './default/es'
import { dateFormats } from './formats'

const STORAGE_KEY = 'lang'

const shortDate = dateFormats

// Money is NOT a number format here. Those follow the UI language, and Spanish
// writes numbers the European way — so switching the console to Spanish turned
// every amount into "5.000,00" while the phone printed "5,000.00" for the same
// figure. Separators are a fact about the country, not the language, so they
// live in `modules/shared/utility/money`.

// Spanish is the product's primary language — the mobile app defaults to it and
// the operators using this console work in Spanish. English stays available and
// remains the fallback, because it is the reference catalogue every other locale
// is translated from, so an untranslated key surfaces readable English rather
// than a raw key path.
export const i18n = createI18n({
  locale: localStorage.getItem(STORAGE_KEY) || 'es',
  fallbackLocale: 'en',
  legacy: false,
  globalInjection: true,
  messages: {
    en,
    es,
  },
  datetimeFormats: {
    en: shortDate,
    es: shortDate,
  },
})

/**
 * Keeps <html lang> honest. It shipped hard-coded to "en" on a console that
 * defaults to Spanish, which tells a screen reader to pronounce Spanish with
 * English phonetics — and tells the browser to offer the wrong translation.
 */
const applyDocumentLanguage = (lang: string): void => {
  document.documentElement.setAttribute('lang', lang)
}


applyDocumentLanguage(i18n.global.locale.value)

export const changeLanguage = (lang: 'en' | 'es'): void => {
  i18n.global.locale.value = lang
  localStorage.setItem(STORAGE_KEY, lang)
  applyDocumentLanguage(lang)
}

const useInternalization = (app: App): void => {
  app.use(i18n)
}

export default useInternalization
